import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { authService } from '../services/api';
import toast from 'react-hot-toast';
import { Camera, RotateCw, ZoomIn, X, Trash2, ImagePlus, Loader2 } from 'lucide-react';

const MAX_AVATAR_SIZE = 2048 * 1024; // 2 MB
const OUTPUT_SIZE = 512;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

async function getCroppedImage(
  imageSrc: string,
  croppedAreaPixels: Area,
  rotation: number,
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const scale = Math.min(1, OUTPUT_SIZE / croppedAreaPixels.width);
  canvas.width = Math.round(croppedAreaPixels.width * scale);
  canvas.height = Math.round(croppedAreaPixels.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-canvas.width / 2, -canvas.height / 2);
  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode image'))),
      'image/jpeg',
      0.9,
    );
  });
}

interface AvatarUploaderProps {
  open: boolean;
  onClose: () => void;
  currentUrl?: string;
  onUploaded: () => Promise<void> | void;
}

const AvatarUploader: React.FC<AvatarUploaderProps> = ({ open, onClose, currentUrl, onUploaded }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
    }
  }, [open]);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  if (!open) return null;

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (JPEG, PNG or WebP).');
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      toast.error('Image must be smaller than 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedImage(imageSrc, croppedAreaPixels, rotation);
      let finalBlob = blob;
      if (blob.size > MAX_AVATAR_SIZE) {
        for (const quality of [0.8, 0.7, 0.6, 0.5]) {
          const canvas = document.createElement('canvas');
          const img = await loadImage(URL.createObjectURL(blob));
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const reEncoded = await new Promise<Blob | null>((resolve) =>
              canvas.toBlob(resolve, 'image/jpeg', quality),
            );
            if (reEncoded && reEncoded.size <= MAX_AVATAR_SIZE) {
              finalBlob = reEncoded;
              break;
            }
          }
        }
      }
      if (finalBlob.size > MAX_AVATAR_SIZE) {
        toast.error('Cropped image is still too large. Try a smaller photo.');
        return;
      }
      const file = new File([finalBlob], 'avatar.jpg', { type: 'image/jpeg' });
      await authService.uploadAvatar(file);
      toast.success('Avatar updated!');
      await onUploaded();
      onClose();
    } catch {
      toast.error('Failed to upload avatar.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await authService.removeAvatar();
      toast.success('Avatar removed.');
      await onUploaded();
      onClose();
    } catch {
      toast.error('Failed to remove avatar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-card shadow-elevated p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-display font-semibold text-foreground m-0">
            {imageSrc ? 'Crop your photo' : 'Profile photo'}
          </h3>
          <button
            className="p-1.5 rounded-full hover:bg-zinc-100 text-zinc-500"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {imageSrc ? (
          <>
            <div className="relative h-64 rounded-lg overflow-hidden bg-zinc-900 mb-4">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="space-y-3 mb-5">
              <label className="flex items-center gap-3 text-sm text-zinc-600">
                <ZoomIn className="w-4 h-4 text-zinc-400 shrink-0" />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full accent-brand-600"
                />
              </label>
              <label className="flex items-center gap-3 text-sm text-zinc-600">
                <RotateCw className="w-4 h-4 text-zinc-400 shrink-0" />
                <input
                  type="range"
                  min={-180}
                  max={180}
                  step={1}
                  value={rotation}
                  onChange={(e) => setRotation(Number(e.target.value))}
                  className="w-full accent-brand-600"
                />
              </label>
            </div>
            <div className="flex gap-3">
              <button
                className="flex-1 h-10 rounded-btn text-sm font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition-colors inline-flex items-center justify-center gap-2"
                onClick={() => setImageSrc(null)}
                disabled={saving}
              >
                <X className="w-4 h-4" /> Cancel
              </button>
              <button
                className="flex-1 h-10 rounded-btn text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                onClick={handleApply}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Apply
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-zinc-100 flex items-center justify-center">
                {currentUrl ? (
                  <img src={currentUrl} alt="Current avatar" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-9 h-9 text-zinc-300" />
                )}
              </div>
              <p className="text-sm text-zinc-500 text-center max-w-[260px]">
                Upload a square photo (max 2 MB). You can crop and rotate it after choosing.
              </p>
            </div>
            <div className="flex gap-3">
              {currentUrl && (
                <button
                  className="h-10 px-4 rounded-btn text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  onClick={handleRemove}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove
                </button>
              )}
              <button
                className="flex-1 h-10 rounded-btn text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 transition-colors inline-flex items-center justify-center gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImagePlus className="w-4 h-4" /> Choose photo
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={onFileSelected}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AvatarUploader;
